import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

const ROLE_CACHE_KEY = 'mentor_portal_user_role';
const USER_CACHE_KEY = 'mentor_portal_user_data';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Try to get user from localStorage on initialization
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [role, setRole] = useState(() => {
    // Try to get role from localStorage on initialization
    try {
      return localStorage.getItem(ROLE_CACHE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const resolveRole = useCallback(async (authUser) => {
    if (!authUser) return null;

    try {
      // First, check if the user exists in the roles table as an admin
      const { data: roleData, error: roleError } = await supabase
        .from('roles')
        .select('role')
        .eq('email', authUser.email)
        .maybeSingle();

      if (!roleError && roleData && roleData.role === 'admin') {
        return 'admin';
      }

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Role resolution timeout')), 5000)
      );

      const rolePromise = (async () => {
        // Try exact match first
        let { data, error } = await supabase
          .from('mentors')
          .select('id, email')
          .eq('email', authUser.email)
          .maybeSingle();

        if (!data && !error) {
          const { data: caseInsensitiveData, error: fetchError } = await supabase
            .from('mentors')
            .select('id, email')
            .ilike('email', authUser.email)
            .maybeSingle();

          if (!fetchError && caseInsensitiveData) {
            data = caseInsensitiveData;
          }
        }

        if (error) {
          console.error('Error determining mentor role:', error);
          return null;
        }

        return data ? 'mentor' : null;
      })();

      try {
        const resolvedRole = await Promise.race([rolePromise, timeoutPromise]);
        return resolvedRole;
      } catch (timeoutError) {
        console.warn('Role resolution timed out, defaulting to no role');
        return null;
      }
    } catch (err) {
      console.error('Exception in role resolution:', err);
      // If role resolution fails, default to null but don't break the auth flow
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;

    const handleSession = async (session) => {
      const authUser = session?.user ?? null;
      if (!active) return;

      // Update user state and cache
      setUser(authUser);
      try {
        if (authUser) {
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(authUser));
        } else {
          localStorage.removeItem(USER_CACHE_KEY);
          localStorage.removeItem(ROLE_CACHE_KEY);
          setRole(null);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.warn('Failed to cache user data:', error);
      }

      // Try to get role from cache first
      let cachedRole = null;
      try {
        cachedRole = localStorage.getItem(ROLE_CACHE_KEY);
      } catch (error) {
        console.warn('Failed to read cached role:', error);
      }

      if (cachedRole && cachedRole !== 'null') {
        // Use cached role
        if (active) setRole(cachedRole);
        if (active) setLoading(false);
      } else {
        // Resolve role from database
        const resolvedRole = await resolveRole(authUser);
        if (active) {
          setRole(resolvedRole);
          // Cache the resolved role
          try {
            if (resolvedRole) {
              localStorage.setItem(ROLE_CACHE_KEY, resolvedRole);
            }
          } catch (error) {
            console.warn('Failed to cache role:', error);
          }
        }
        if (active) setLoading(false);
      }
    };

    (async () => {
      const { data } = await supabase.auth.getSession();
      await handleSession(data.session);
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, session) => {
      await handleSession(session);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [resolveRole]);

  const signIn = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setLoading(false);
        throw error;
      }

      // Wait a bit for the session to be established
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get the current session to ensure it's established
      await supabase.auth.getSession();

      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      // Clear local state and cache immediately
      setUser(null);
      setRole(null);
      setLoading(false);
      try {
        localStorage.removeItem(USER_CACHE_KEY);
        localStorage.removeItem(ROLE_CACHE_KEY);
      } catch (error) {
        console.warn('Failed to clear cache:', error);
      }
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      role,
      loading,
      signIn,
      signOut,
    }),
    [user, role, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
